import { useContext, useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { UserDataContext } from "../app/App";
import { Paper, Box, TextField, Typography, FormControl, OutlinedInput, FormLabel, RadioGroup, FormControlLabel, Radio, CircularProgress, FormHelperText } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import ErrorPage from "./ErrorPage";
import Layout from "../components/Layout/Layout";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import { useGetProjectLine, useSubmitIncidentReportForm } from "../api";
import UploadImagesButton from "../components/buttons/UploadImagesButton/index";

function ReportIncident() {
    const initialFormFields = {
        name: {
            label: "Your name",
            name: "name",
            type: "text",
            value: "",
            placeholder: "Enter name",
            required: true,
            visible: true,
            readOnly: true,
        },
        equipmentNumber: {
            label: "Identify Garðlist equipment by entering the car or machine number",
            name: "equipmentNumber",
            type: "text",
            value: "",
            placeholder: "Enter car/equipment number",
            required: true,
            visible: true,
        },
        accidentType: {
            label: "Type of Accident: Select the appropriate category",
            type: "radio",
            name: "accidentType",
            options: [
                { label: "House, property, or vehicle damage", value: "house_property", reveals: ["damageToCar", "calledArekstur", "accidentTime", "ownerPresent"] },
                { label: "Garden and plant life damage", value: "garden_plantlife", reveals: ["accidentTime", "ownerPresent"] },
                { label: "Damage to our own equipment or property", value: "own_machine_property", reveals: ["damageToCar", "calledArekstur", "accidentTime", "accidentDescription"] },
            ],
            value: null,
            required: true,
            visible: true,
        },
        damageToCar: {
            label: "Was any vehicle or machine not belonging to Garðlist damaged?",
            type: "instruction",
            name: "damageToCar",
            instructionText: "In case of a car accident, immediate reporting is essential. Please file your report promptly at www.arekstur.is, or contact them directly at 587-9090.",
            visible: false,
        },
        calledArekstur: {
            label: "Called Arekstur.is (578-9090)?",
            type: "radio",
            name: "calledArekstur",
            options: [
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
            ],
            value: null,
            required: true,
            visible: false,
        },
        accidentTime: {
            label: "When did it happen?",
            type: "radio",
            name: "accidentTime",
            options: [
                { label: "Right now", value: "right_now" },
                { label: "Earlier today", value: "earlier_today" },
                { label: "Earlier", value: "earlier_this_week" },
            ],
            value: null,
            required: true,
            visible: false,
        },
        ownerPresent: {
            label: "Is the property or vehicle owner nearby?",
            type: "radio",
            name: "ownerPresent",
            options: [
                { label: "Yes", value: "yes", reveals: ["ownerName", "ownerIdentifier", "ownerPhone", "ownerAddress", "accidentDescription", "uploadPictures"] },
                { label: "No", value: "no", reveals: ["ownerAddress", "accidentDescription", "uploadPictures"] },
            ],
            value: null,
            required: true,
            visible: false,
        },
        ownerName: {
            label: "Owner's Name",
            name: "ownerName",
            type: "text",
            value: "",
            placeholder: "Enter owner's name",
            required: false,
            visible: false,
        },
        ownerIdentifier: {
            label: "Owner's ID / Kennitala",
            name: "ownerIdentifier",
            type: "kennitala",
            pattern: /^\d{0,10}$/,
            validatorPattern: /^\d{10}$/,
            errorText: "Kennitala must be 10 digits long",
            value: "",
            placeholder: "Enter owner's ID (Kennitala)",
            required: false,
            visible: false,
        },
        ownerPhone: {
            label: "Owner's Phone",
            name: "ownerPhone",
            type: "phone",
            value: "",
            pattern: /^\d{0,7}$/,
            validatorPattern: /^\d{7}$/,
            errorText: "Phone number must be 7 digits long",
            placeholder: "Enter phone number",
            required: false,
            visible: false,
        },
        ownerAddress: {
            label: "Owner's address / Accident location",
            name: "ownerAddress",
            type: "text",
            value: "",
            placeholder: "Enter address or location",
            required: true,
            visible: false,
        },
        accidentDescription: {
            label: "What happened?",
            name: "accidentDescription",
            type: "textarea",
            value: "",
            placeholder: "Describe the accident",
            required: true,
            visible: false,
        },
    };

    const uploadButtonRef = useRef();
    const history = useHistory();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useContext(UserDataContext);
    const { id: projectLineId } = useParams();

    const [incidentReport, setIncidentReport] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [formFields, setFormFields] = useState({
        ...initialFormFields,
        name: {
            ...initialFormFields.name,
            value: user?.name ?? "",
        },
    });

    const { data: projectLine, isLoading: isLoadingProjectLine, isError: isErrorProjectLine } = useGetProjectLine(projectLineId, { enabled: !!projectLineId });

    useEffect(() => {
        let updatedFormFields = { ...initialFormFields };

        if (projectLine && Object.keys(projectLine).length > 0) {
            updatedFormFields = {
                ...updatedFormFields,
                ownerAddress: {
                    ...updatedFormFields.ownerAddress,
                    value: projectLine?.project?.address?.name ?? "",
                },
                equipmentNumber: {
                    ...updatedFormFields.equipmentNumber,
                    value: projectLine?.vehicle?.number ?? "",
                },
            };
        }

        // Update the name field based on the current user
        updatedFormFields = {
            ...updatedFormFields,
            name: {
                ...updatedFormFields.name,
                value: user?.name ?? "",
            },
        };

        setFormFields(updatedFormFields);

        // Cleanup function to reset form fields on unmount
        return () => {
            setFormFields(initialFormFields);
        };
    }, [projectLine, user]);

    const { mutate: submitReport, isLoading } = useSubmitIncidentReportForm({
        onSuccess: async (createdReport) => {
            if (createdReport && createdReport?.damageNumber) {
                setIncidentReport(createdReport);

                if (uploadButtonRef.current && uploadButtonRef.current.uploadImages) {
                    // Pass the created report explicitly - setIncidentReport above has not
                    // re-rendered the upload button yet, so its prop is still null here.
                    await uploadButtonRef.current.uploadImages(null, createdReport);
                }

                // Once images are uploaded, then show the success message and navigate
                enqueueSnackbar("Accident reported successfully", {
                    variant: "success",
                    anchorOrigin: { vertical: "bottom", horizontal: "center" },
                    autoHideDuration: 3000,
                });
                setFormFields(initialFormFields);
                history.goBack();
            }
        },
        onError: () => {
            enqueueSnackbar(`Error reporting accident`, {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
        },
    });

    const validateFormFieldValue = ({ field, value }) => {
        if (!field) return;
        let errorMessage = "";

        if (field.required && value.trim() === "") {
            errorMessage = field.errorText || "This field is required";
        }
        if (field.validatorPattern) {
            const passed = field.validatorPattern.test(value);

            if (!passed && value !== "") {
                errorMessage = field.errorText || "Invalid value";
            } else if (field.required && value.trim() === "") {
                errorMessage = field.errorText || "This field is required";
            } else {
                errorMessage = "";
            }
        }
        setFormErrors((prev) => ({
            ...prev,
            [field.name]: errorMessage,
        }));
    };

    const isFormValid = () => {
        const hasErrors = Object.values(formErrors).filter((value) => value !== "").length > 0;
        return !hasErrors && Object.values(formFields).filter((field) => field?.visible && field?.required && !field?.value).length === 0;
    };

    const handleChangeFormFieldValue = ({ field, newValue }) => {
        if (!field || (!newValue && newValue !== "")) {
            if (!field) console.error("form field is undefined.");
            if (!newValue) console.error("cannot set form field value: value is undefined or empty.");
            return;
        }
        if (field?.type === "instruction") return;
        setFormFields((prevFormFields) => ({
            ...prevFormFields,
            [field.name]: { ...prevFormFields[field.name], value: newValue },
        }));

        validateFormFieldValue({ field, value: newValue });
        handleRadioRevealVisibleFields({ field, newValue });
    };
    const handleRadioRevealVisibleFields = ({ field, newValue }) => {
        if (field?.type !== "radio" || !field || !newValue) return;
        if (!field?.options) {
            console.error("form field of type radio has no options.");
            return;
        }

        setFormFields((prevFormFields) => {
            const fieldNamesToReveal = getSelectedRadioRevealableFieldNames(field, newValue);
            const allFieldRevealableFieldNames = getAllRadioRevealableFieldNames(field);

            let updatedFields = { ...prevFormFields };
            revealRadioFields(fieldNamesToReveal, updatedFields);

            allFieldRevealableFieldNames.forEach((fieldName) => {
                if (!fieldNamesToReveal.includes(fieldName)) {
                    if (updatedFields[fieldName]?.visible === true) {
                        updatedFields[fieldName].visible = false;
                    }
                }
            });
            return updatedFields;
        });

        if (field?.reveals) {
            setFormFields((prevFormFields) => {
                let updatedFields = { ...prevFormFields };
                const fieldNamesToReveal = field.reveals || [];

                if (newValue === "no") {
                    // Hide the fields specified in the 'reveals' array
                    fieldNamesToReveal.forEach((fieldName) => {
                        if (updatedFields[fieldName]) {
                            updatedFields[fieldName].visible = false;
                        }
                    });
                } else {
                    // Show the fields specified in the 'reveals' array
                    fieldNamesToReveal.forEach((fieldName) => {
                        if (updatedFields[fieldName]) {
                            updatedFields[fieldName].visible = true;
                        }
                    });
                }

                return updatedFields;
            });
        }
    };
    const revealRadioFields = (fieldNamesToReveal, updatedFields) => {
        if (fieldNamesToReveal.length === 0) return;

        fieldNamesToReveal.forEach((fieldName) => {
            const currentField = updatedFields[fieldName];
            if (currentField) {
                if (currentField?.visible === false) {
                    currentField.visible = true;
                }
                if (currentField?.value !== null) {
                    const nestedRevealableFieldNames = getSelectedRadioRevealableFieldNames(currentField, currentField.value);

                    fieldNamesToReveal.push(...nestedRevealableFieldNames);
                    revealRadioFields(nestedRevealableFieldNames, updatedFields);
                }
            }
        });
    };

    const getSelectedRadioRevealableFieldNames = (field, selectedValue) => {
        if (field?.type !== "radio" || !field?.options) return [];

        const selectedOption = field?.options.find((option) => option?.value === selectedValue) || [];
        return selectedOption?.reveals || [];
    };
    const getAllRadioRevealableFieldNames = (field) => {
        if (field?.type !== "radio" || !field?.options) return [];

        return field.options.reduce((all, option) => {
            if (option?.reveals) {
                return all.concat(option.reveals);
            }
            return all;
        }, []);
    };

    const handleSumbit = (event) => {
        event.preventDefault();

        if (!isFormValid()) return;

        const formFieldValues = Object.keys(formFields).reduce((fieldValues, fieldName) => {
            fieldValues[fieldName] = formFields[fieldName].value;
            return fieldValues;
        }, {});

        const payload = {
            ProjectLineId: projectLineId ?? null,
            LocationId: projectLine?.project?.address?.id ?? null,
            VehicleId: projectLine?.vehicle?.id ?? null,
            LocationName: formFieldValues?.ownerAddress ?? null,
            UserId: user?.id ?? null,
            IsDamageToEquipment: formFieldValues?.damageToCar === "yes" ?? null,
            OwnerId: formFieldValues?.ownerIdentifier ?? null,
            OwnerPhone: formFieldValues?.ownerPhone ?? null,
            DamageType: formFieldValues?.accidentType ?? null,
            IsOwnerPresent: formFieldValues?.ownerPresent === "yes" ?? null,
            TimeOfIncident: formFieldValues?.accidentTime ?? null,
            ReportedToArekstur: formFieldValues?.calledArekstur === "yes" ?? null,
            UserDescription: formFieldValues?.accidentDescription ?? null,
            Concluded: false,
            OwnerName: formFieldValues?.ownerName ?? null,
        };
        submitReport(payload);
    };

    let labelCounter = 1;
    const createFormFieldLabel = (field) => {
        if (!field?.label) return;

        const label = `${labelCounter}. ${field.label}`;
        labelCounter++;

        if (!field.required) {
            return (
                <>
                    {label}{" "}
                    <Typography component="span" style={{ color: "gray" }}>
                        (optional)
                    </Typography>
                </>
            );
        }
        return label;
    };

    if (isErrorProjectLine) {
        return <ErrorPage />;
    }
    if (isLoadingProjectLine) {
        return (
            <Layout title={t("ReportIncident")} subtitle={user.name} color="rgb(16 185 129)">
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    return (
        <Layout color="rgb(16 185 129)" title={t("ReportIncident")} subtitle={projectLine?.project?.address?.name ?? user?.name ?? ""}>
            <Box display="flex" flexDirection="column" style={{ gap: "2rem", padding: "0.5rem" }}>
                <Box bgcolor="#f5f5f5" p={2} position="relative">
                    <InfoIcon color="primary" style={{ position: "absolute", top: 8, right: 8 }} />
                    <Box component={Paper} variant="outlined" elevation={0} p={1}>
                        <Typography variant="body1">
                            <strong>Important:</strong>
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            - In cases where damage involves both our equipment and another property or vehicle, <u>please complete two separate reports.</u>
                        </Typography>
                        <Typography variant="body2">- Submit one report for the external property or vehicle, and a second report for our equipment“</Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" gutterBottom>
                        Report Form
                    </Typography>
                    <form onSubmit={handleSumbit} autoComplete="off">
                        <Box display="flex" flexDirection="column" style={{ gap: "1rem" }}>
                            {Object.values(formFields).map(
                                (field, index) =>
                                    field.visible && (
                                        <Box key={field.name}>
                                            {(field.type === "text" || field.type === "number" || field.type === "kennitala" || field.type === "phone") && (
                                                <FormControl margin="dense" variant="outlined" error={!!formErrors[field.name]} fullWidth>
                                                    <FormLabel htmlFor={field.name} style={{ marginBottom: "0.75rem" }} component="legend">
                                                        {field.name === "calledArekstur" ? (
                                                            <>
                                                                {index + 1}. Called Arekstur.is (
                                                                <a href="tel:+3545879090" style={{ marginLeft: "4px" }}>
                                                                    587-9090
                                                                </a>
                                                                )?
                                                            </>
                                                        ) : (
                                                            <>{createFormFieldLabel(field)}</>
                                                        )}
                                                    </FormLabel>

                                                    <OutlinedInput
                                                        style={{ backgroundColor: `${field?.readOnly ? "#d3d3d3s" : "#fff"}` }}
                                                        id={field.name}
                                                        name={field.name}
                                                        value={field.value}
                                                        error={!!formErrors[field.name]}
                                                        placeholder={field.placeholder}
                                                        onChange={(event) => {
                                                            if (field?.pattern && event.target.value !== "" && !field.pattern.test(event.target.value)) {
                                                                return;
                                                            }
                                                            handleChangeFormFieldValue({ field, newValue: event.target.value });
                                                        }}
                                                        required={field.required}
                                                        readOnly={!!field?.readOnly}
                                                        disabled={!!field?.readOnly}
                                                        margin="dense"
                                                        fullWidth
                                                    />
                                                    <FormHelperText>{formErrors[field.name]}</FormHelperText>
                                                </FormControl>
                                            )}

                                            {/* Radio options */}
                                            {field.type === "instruction" && (
                                                <Box component={Paper} variant="outlined" elevation={0} p={2}>
                                                    <Typography variant="body1" style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>
                                                        {field?.name === "damageToCar" ? (
                                                            <>
                                                                In case of a car accident, immediate reporting is essential. Please file your report promptly at www.arekstur.is, or contact them
                                                                directly at
                                                                <a href="tel:+3545789090" style={{ marginLeft: "4px" }}>
                                                                    578-9090
                                                                </a>
                                                            </>
                                                        ) : (
                                                            <>{field.instructionText}</>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Radio options */}
                                            {field.type === "radio" && (
                                                <FormControl component="fieldset" margin="dense" fullWidth variant="outlined">
                                                    <FormLabel htmlFor={field.name} component="legend">
                                                        {createFormFieldLabel(field)}
                                                    </FormLabel>
                                                    <RadioGroup name={field.name} value={field.value} onChange={(event) => handleChangeFormFieldValue({ field, newValue: event.target.value })}>
                                                        {field.options &&
                                                            field.options.map((option) => <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />)}
                                                    </RadioGroup>
                                                </FormControl>
                                            )}

                                            {/* Text area */}
                                            {field.type === "textarea" && (
                                                <TextField
                                                    key={field.name}
                                                    name={field.name}
                                                    value={field.value}
                                                    placeholder={field.placeholder}
                                                    required={field.required}
                                                    label={createFormFieldLabel(field)}
                                                    onChange={(event) => {
                                                        handleChangeFormFieldValue({ field, newValue: event.target.value });
                                                    }}
                                                    minRows={4}
                                                    multiline
                                                    fullWidth
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        </Box>
                                    )
                            )}
                        </Box>

                        <Box bgcolor="#f5f5f5" mt={2} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                            <UploadImagesButton
                                ref={uploadButtonRef}
                                asIconButton={false}
                                loading={isLoading}
                                projectLine={projectLine}
                                incidentReport={incidentReport}
                                onUploadComplete={() => {
                                    console.info("Images uploaded successfully");
                                }}
                            />
                            <Box>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    loading={isLoading}
                                    disabled={!isFormValid() || isLoading}
                                    title={!isFormValid() ? "Please fill in all required fields" : "Submit"}
                                    fullWidth
                                >
                                    Submit
                                </LoadingButton>
                            </Box>

                            <Typography variant="body2" style={{ marginTop: "1rem" }}>
                                Images are uploaded when the report is submitted.
                            </Typography>
                        </Box>
                    </form>
                </Box>
            </Box>
        </Layout>
    );
}

export default ReportIncident;
